import React, { useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { QUICK_MARKETS, SINGLE_DIGITS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect from "./MatkaSessionSelect";

export default function MatkaSingleDigitBulk({
  selectedMarket,
  selectedGameType,
  sessionStatus,
  onSessionStatusChange,
  digitPoints,
  onPointChange,
  totalBids,
  totalPoints,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onPlaceBet,
  onSelectTab,
  onClearPoints,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  // Target Digits Selection (default to 1 and 4 as shown in the screenshot)
  const [targetDigits, setTargetDigits] = useState(["1", "4"]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: sessionStatus,
    digitPoints,
    onPlaced: onClearPoints,
  });

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  const toggleTargetDigit = (digit) => {
    setTargetDigits((prev) =>
      prev.includes(digit)
        ? prev.filter((d) => d !== digit)
        : [...prev, digit]
    );
  };

  const row1TargetDigits = ["1", "2", "3", "4", "5"];
  const row2TargetDigits = ["6", "7", "8", "9", "0"];

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Bar for Betting Screen */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Betting Screen Body */}
      <main className="container-fluid custom-padding matka-betting-body pt-3 pb-4">
        {/* Back to Market Link */}
        <button
          type="button"
          className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
          onClick={onBackToGameTypes}
        >
          <i className="bi bi-chevron-left me-1"></i>
          Back to{" "}
          {selectedMarket.name.charAt(0) +
            selectedMarket.name.slice(1).toLowerCase()}
        </button>

        {/* Title */}
        <h1 className="matka-betting-title mb-3">Single Digit Bulk</h1>

        {/* Game Type (Open/Close) Selector */}
        <div className="matka-game-type-selector-wrapper mb-4">
          <label className="matka-game-type-label">GAME TYPE</label>
          <MatkaSessionSelect
            betDirection={selectedGameType?.betDirection}
            value={sessionStatus}
            onChange={onSessionStatusChange}
          />
        </div>

        {/* Select Target Digits Card */}
        <div className="matka-target-digits-card mb-4">
          <div className="matka-target-digits-header">
            <h3 className="matka-target-digits-title">Select Target Digits</h3>
            <p className="matka-target-digits-hint">
              Pick digits below to generate Single Panna combinations
            </p>
          </div>

          <div className="matka-target-digits-grid">
            {/* Row 1: 1 to 5 */}
            <div className="matka-target-digits-row">
              {row1TargetDigits.map((digit) => (
                <button
                  key={digit}
                  type="button"
                  className={`matka-target-digit-btn ${
                    targetDigits.includes(digit) ? "active" : ""
                  }`}
                  onClick={() => toggleTargetDigit(digit)}
                >
                  {digit}
                </button>
              ))}
            </div>

            {/* Row 2: 6 to 0 */}
            <div className="matka-target-digits-row">
              {row2TargetDigits.map((digit) => (
                <button
                  key={digit}
                  type="button"
                  className={`matka-target-digit-btn ${
                    targetDigits.includes(digit) ? "active" : ""
                  }`}
                  onClick={() => toggleTargetDigit(digit)}
                >
                  {digit}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Choose Digit & Enter Points Section */}
        <div className="matka-digits-section mb-4">
          <h3 className="matka-digits-section-heading mb-3">
            CHOOSE DIGIT &amp; ENTER POINTS
          </h3>

          <div className="matka-digits-grid">
            {SINGLE_DIGITS.map((digit) => (
              <div
                key={digit}
                className={`matka-digit-item-card ${
                  digitPoints[digit] ? "active-input" : ""
                }`}
              >
                <div className="matka-digit-badge">{digit}</div>
                <input
                  type="text"
                  inputMode="numeric"
                  className="matka-digit-points-input"
                  placeholder="Enter Points"
                  value={digitPoints[digit] || ""}
                  onChange={(e) => onPointChange(digit, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* In-page Action Bar with Bids, Points, and CONTINUE */}
        <div className="matka-bulk-summary-bar">
          <div className="d-flex align-items-center gap-3">
            {/* Bids Counter */}
            <div className="matka-bulk-counter-pill">
              <span className="matka-bulk-counter-val">
                {totalBids > 0 ? totalBids : 10}
              </span>
              <span className="matka-bulk-counter-label">Bids</span>
            </div>

            {/* Total Points Counter */}
            <div className="matka-bulk-counter-pill">
              <span className="matka-bulk-counter-val">
                {totalPoints > 0 ? totalPoints.toLocaleString() : "2,225"}
              </span>
              <span className="matka-bulk-counter-label">Total Points</span>
            </div>
          </div>

          {/* Continue Action Button */}
          <button
            type="button"
            className="matka-bulk-continue-btn"
            onClick={handleContinue}
            disabled={totalBids === 0}
          >
            CONTINUE
          </button>
        </div>
      </main>

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
